import { InfoCircleFilled } from '@ant-design/icons';
import {
  Box,
  Button,
  Card,
  Chip,
  Grid,
  ListItemText,
  MenuItem,
  MenuList,
  Stack,
  TextField as TextFieldMui
} from '@mui/material';
import MainCard from 'components/MainCard';
import HTMLBlock from 'components/input/HTMLBlockFormik';
import { Field, Form, Formik } from 'formik';
import { Autocomplete, TextField } from 'formik-mui';
import useSaveObject from 'hooks/data-saving/useSaveObject';
import useAuth from 'hooks/useAuth';
import { merge } from 'lodash';
import { useEffect, useState } from 'react';
import { dispatch, useSelector } from 'store';
import { setEmailTemplateBlasts } from 'store/reducers/config';
import AutoSubmit from 'utils/AutoSubmit';

const EmailTemplateBlasts = () => {
  const { db } = useAuth();
  const [selected, setSelected] = useState<string | null>(null);
  const { saveObject } = useSaveObject();
  const { emailTemplateBlasts = [] } = useSelector((state) => state.config.mainConfig);
  const { mailLists } = useSelector((state) => state.config.mainConfig);

  useEffect(() => {
    if (!db) return;
    const unsubscribe = db
      .collection('templateEmailBlasts')
      .orderBy('name')
      .onSnapshot((snap) => {
        if (snap.empty) return;
        dispatch(
          setEmailTemplateBlasts(
            snap.docs.map((doc) => {
              return { ...doc.data(), id: doc.id };
            })
          )
        );
      });

    return () => unsubscribe();
  });

  const emailTemplateInit = {
    id: '',
    name: '',
    html: '',
    subject: '',
    mailingList: '',
    lastSent: null
  };

  const handleMenuClick = (id: string) => {
    setSelected(id);
  };

  const handleSendBlast = () => {};

  const current = emailTemplateBlasts.filter((item) => item.id === selected);
  const merged = merge({}, emailTemplateInit, current[0]);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={4}>
        <Card sx={{ padding: 2, mb: 2 }}>
          <h3>Email Templates</h3>
          <MenuList sx={{ marginBottom: 2 }}>
            {emailTemplateBlasts &&
              emailTemplateBlasts.map((template, index) => (
                <MenuItem
                  key={index}
                  selected={selected === template.id}
                  onClick={() => handleMenuClick(template.id || '')}
                >
                  <ListItemText>{template.name}</ListItemText>
                </MenuItem>
              ))}
          </MenuList>
        </Card>
        <Chip
          icon={<InfoCircleFilled />}
          label={
            <>
              You can use
              <code style={{ color: 'orange' }}>[event-date]</code>,
              <code style={{ color: 'orange' }}>[event-title]</code>, and
              <code style={{ color: 'orange' }}>[venue-name]</code> to substitute the relevant event
              information into the email.
            </>
          }
          variant="outlined"
          sx={{
            height: 'auto',
            '& .MuiChip-label': {
              display: 'block',
              whiteSpace: 'normal',
              padding: 2
            }
          }}
        />
      </Grid>
      {selected && (
        <Grid item xs={12} md={8}>
          <Formik
            enableReinitialize
            initialValues={merged}
            onSubmit={(values, { setSubmitting }) => {
              saveObject('templateEmailBlasts', selected, values || {}, merged || []);
              setSubmitting(false);
            }}
          >
            {({ values, setFieldValue }) => {
              return (
                <MainCard>
                  <AutoSubmit />
                  <Form id="currentTab">
                    <Stack spacing={2}>
                      <Field component={TextField} name="name" label="Name" fullWidth />
                      <Field
                        name="mailingList"
                        fullWidth
                        component={Autocomplete}
                        options={mailLists || []}
                        getOptionLabel={(option: { name: string }) => option.name || ''}
                        renderInput={(params: any) => (
                          <TextFieldMui
                            {...params}
                            name="mailingList"
                            label="Mailing List"
                            variant="outlined"
                          />
                        )}
                      />

                      <Field component={TextField} name="subject" label="Subject" fullWidth />
                      <HTMLBlock
                        values={values}
                        setFieldValue={setFieldValue}
                        path="html"
                        deps={[values.id]}
                      />
                      <Stack direction="row">
                        <Box sx={{ ml: 'auto' }}>
                          <Button variant="outlined" size="medium" onClick={handleSendBlast}>
                            Send Blast
                          </Button>
                        </Box>
                      </Stack>
                    </Stack>
                  </Form>
                </MainCard>
              );
            }}
          </Formik>
        </Grid>
      )}
    </Grid>
  );
};

export default EmailTemplateBlasts;
